import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayers } from '@agissc/ktag-map';
import useBasemapCategories from './api/useBasemapCategories';
import useAppState from '../../AppStore';
import BasemapItem from './BasemapItem';
import LayerVisibility from './VisiblitySettings';
import { useTracking } from '../../utils/tracking/TrackingContext';
import List from '../../components/list/List';
import { YearPicker } from './YearPicker';
import BasemapPlaceholder from './BasemapPlaceholder';
import BasemapSelectionWrapper from './BasemapSelectionWrapper';

const yearRegex = /\d{4}|aktuell/;
const basemapPreviewImageUrl = 'assets/images/';

const BasemapSelection = () => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();

  const basemapOpacity = useAppState((state) => state.basemapOpacity);
  const setBasemapOpacity = useAppState((state) => state.setBasemapOpacity);
  const isBasemapVisible = useAppState((state) => state.isBasemapVisible);
  const toggleBasemapVisibility = useAppState((state) => state.toggleBasemapVisibility);
  const addNotification = useAppState((state) => state.addNotification);
  const selectedBasemapId = useAppState((state) => state.selectedBasemapId);
  const errorLayers = useAppState((state) => state.errorLayers);
  const setSelectedBasemapCategory = useAppState(
    (state) => state.setSelectedBasemapCategory,
  );

  const layers = useLayers();
  const categoriesQuery = useBasemapCategories();

  const layersLookup = layers.data;
  const categoriesLookup = categoriesQuery.data ?? {};
  const aerialCategory = categoriesLookup.aerial ?? {};

  const [currentBasemap, setCurrentBasemap] = useState(selectedBasemapId);

  useEffect(() => {
    if (selectedBasemapId === currentBasemap) return;

    setCurrentBasemap(selectedBasemapId);

    if (errorLayers.includes(selectedBasemapId)) {
      addNotification({
        id: Date.now().toString(),
        title: t('basemap'),
        message: t('notification_topic_loading_error', {
          topicLayerName: layersLookup[selectedBasemapId].title,
        }),
        type: 'negative',
      });
    }
  }, [selectedBasemapId, errorLayers]);

  useEffect(() => {
    if (isBasemapVisible) return;

    addNotification({
      id: Date.now().toString(),
      title: t('notification_basemap_invisible_title'),
      message: t('notification_basemap_invisible_description'),
      type: 'information',
    });
  }, [isBasemapVisible]);

  if (layers.isLoading || categoriesQuery.isLoading)
    return (
      <BasemapSelectionWrapper>
        <BasemapPlaceholder />
      </BasemapSelectionWrapper>
    );

  const selectedBasemapCategory = Object.entries(categoriesLookup).find(([, category]) =>
    category.items.includes(selectedBasemapId!),
  )?.[0];

  const basemapsByYear: { [year: string]: string } = Object.values(
    aerialCategory.items,
  ).reduce((acc: { [year: string]: string }, item: string) => {
    const match = item.match(yearRegex);
    if (!match) return acc;
    const baseLayer = layersLookup[item];
    const key = baseLayer.title.match(yearRegex)?.[0] ?? match[0];
    acc[key] = acc[key] || item;
    return acc;
  }, {});

  const updateSelectedBasemap = (categoryId: string, itemId?: string) => {
    const basemapId = itemId ?? categoriesLookup[categoryId]?.defaultItem;
    Tracking.Basemap.addBasemap(basemapId);
    setSelectedBasemapCategory(categoryId, basemapId);
  };

  const handleToggleVisibility = () => {
    Tracking.Basemap.setVisibility(isBasemapVisible ? 'hide' : 'show', selectedBasemapId);
    toggleBasemapVisibility();
  };

  const handleOpacityChange = (value: number) => {
    if (basemapOpacity !== value) {
      Tracking.Basemap.setOpacity(value, selectedBasemapId);
      setBasemapOpacity(value);
    }
  };

  const items = Object.entries(categoriesLookup).map(([key, value]) => ({
    id: key,
    ...value,
  }));

  return (
    <BasemapSelectionWrapper>
      <List
        ulClassName="basemap-selection__items"
        items={items}
        renderItem={(item) => (
          <BasemapItem
            key={item.id}
            isActive={selectedBasemapCategory === item.id}
            title={t(item.label)}
            previewImage={`${basemapPreviewImageUrl}preview-${item.id}.png`}
            onSelect={() => updateSelectedBasemap(item.id)}
          />
        )}
      />
      <YearPicker
        layers={basemapsByYear}
        onSelectYear={(year) => updateSelectedBasemap('aerial', basemapsByYear[year])}
        selectedBaseMap={selectedBasemapId}
      />
      <div className="basemap__settings">
        <LayerVisibility
          additionalClassNames="basemap__visibility"
          isVisible={isBasemapVisible}
          onToggleVisibility={handleToggleVisibility}
          onOpacityChanged={handleOpacityChange}
          opacity={basemapOpacity}
        />
      </div>
    </BasemapSelectionWrapper>
  );
};

export default BasemapSelection;
